import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, CreditCard, Smartphone, CheckCircle, XCircle } from 'react-feather';
import { formatCurrencyWithConversion } from '../utils/currency';
import { depositsAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import './DepositCheckout.css';

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 75; // ~5 minutes

export default function DepositCheckout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { socket } = useSocket();
  const checkout = location.state;

  const isDirectPay = checkout?.isDirectPay === true;
  const isFapshiDirect = isDirectPay && checkout?.gatewayId === 'fapshi';
  const isSwychrDirect = isDirectPay && checkout?.gatewayId === 'swychr';

  // Direct Pay state
  const [phone, setPhone] = useState('');
  const [depositing, setDepositing] = useState(false);
  const [depositError, setDepositError] = useState('');
  const storageKey = isSwychrDirect ? 'nb_swychr_pending' : 'nb_fapshi_pending';
  const [pendingId, setPendingId] = useState(() => {
    return sessionStorage.getItem(storageKey) || null;
  });
  const [pollStatus, setPollStatus] = useState(null); // 'pending' | 'completed' | 'failed'
  const [networkError, setNetworkError] = useState(false);
  const pollRef = useRef(null);
  const pollCountRef = useRef(0);

  // Real-time socket listener for instant deposit completion
  useEffect(() => {
    if (!socket || !pendingId) return;

    socket.emit('join_deposit', pendingId);

    const onCompleted = (data) => {
      if (!data || !data.deposit_id || String(data.deposit_id) === String(pendingId)) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setPollStatus('completed');
        setPendingId(null);
        setNetworkError(false);
        refreshUser({ force: true });
      }
    };

    socket.on(`deposit_${pendingId}`, onCompleted);
    socket.on('deposit_completed', onCompleted);

    return () => {
      socket.off(`deposit_${pendingId}`, onCompleted);
      socket.off('deposit_completed', onCompleted);
    };
  }, [socket, pendingId, refreshUser]);

  // Persist pendingId to survive accidental page refreshes
  useEffect(() => {
    if (pendingId) {
      sessionStorage.setItem(storageKey, pendingId);
    } else {
      sessionStorage.removeItem(storageKey);
    }
  }, [pendingId, storageKey]);

  // Redirect if no checkout state and not direct-pay
  useEffect(() => {
    if (!isDirectPay && !checkout?.paymentUrl) {
      navigate('/dashboard/deposit', { replace: true });
    }
  }, [checkout, navigate, isDirectPay]);

  // Auto-navigate back after successful payment
  useEffect(() => {
    if (pollStatus === 'completed') {
      const timer = setTimeout(() => {
        navigate('/dashboard/deposit');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [pollStatus, navigate]);

  // Polling for Direct Pay
  useEffect(() => {
    if (!pendingId) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    pollCountRef.current = 0;

    const poll = async () => {
      try {
        let status = '';
        if (isSwychrDirect) {
          const res = await depositsAPI.verifySwychr(pendingId);
          status = (res.data?.status || '').toUpperCase();
        } else {
          const res = await depositsAPI.fapshiDirectPayStatus(pendingId);
          status = (res.data?.status || '').toUpperCase();
        }
        setNetworkError(false);

        if (status === 'SUCCESSFUL' || status === 'COMPLETED') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPollStatus('completed');
          setPendingId(null);
          refreshUser({ force: true });
        } else if (status === 'FAILED' || status === 'FAILLED') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPollStatus('failed');
          setPendingId(null);
          setDepositError('Mobile Money payment was declined or failed. Please try again.');
        }
      } catch (err) {
        // Fallback: If poll endpoint throws or network drops, check user deposit history
        pollCountRef.current += 1;
        if (pollCountRef.current % 3 === 0) {
          try {
            const hRes = await depositsAPI.history();
            const matching = (hRes.data?.deposits || []).find((d) => String(d.id) === String(pendingId));
            if (matching && matching.status === 'completed') {
              clearInterval(pollRef.current);
              pollRef.current = null;
              setPollStatus('completed');
              setPendingId(null);
              setNetworkError(false);
              refreshUser({ force: true });
              return;
            }
          } catch (_) {}
        }
        setNetworkError(true);
      }
    };

    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [pendingId, refreshUser, isSwychrDirect]);

  const handleDirectDeposit = async () => {
    const normalised = phone.trim().replace(/\D/g, '');
    if (!normalised) {
      setDepositError('Please enter your Mobile Money phone number.');
      return;
    }
    setDepositError('');
    setDepositing(true);
    try {
      let res;
      if (isSwychrDirect) {
        res = await depositsAPI.initiateSwychr({
          amount_local: checkout.localAmount || checkout.xafAmount,
          country_code: checkout.countryCode || 'CM',
          currency: checkout.currency || 'XAF',
          phone: normalised,
        });
      } else {
        res = await depositsAPI.fapshiDirectPay(checkout.xafAmount, normalised);
      }
      setPendingId(String(res.data.deposit_id));
      setPollStatus('pending');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      setDepositError(msg || 'Failed to send payment request. Check your number and try again.');
    } finally {
      setDepositing(false);
    }
  };

  const openProviderCheckout = () => {
    window.location.href = checkout.paymentUrl;
  };

  if (!isDirectPay && !checkout?.paymentUrl) return null;

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="deposit-checkout-top-actions">
        <button
          className="btn btn-secondary deposit-checkout-back-btn"
          type="button"
          onClick={() => navigate('/dashboard/deposit')}
        >
          <ArrowLeft size={16} /> Back to Deposit
        </button>
      </div>

      <h1 className="page-title">Checkout</h1>
      <p className="page-subtitle">
        {isDirectPay
          ? 'Enter your Mobile Money number to receive an instant payment push on your phone.'
          : 'Review your deposit, then continue to the payment provider.'}
      </p>

      <div className="card deposit-checkout-card">
        {/* Header */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 20 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: 'var(--accent-glow)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--accent)',
            flexShrink: 0,
          }}>
            {isDirectPay ? <Smartphone size={22} /> : <CreditCard size={22} />}
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)' }}>
              {checkout.gateway === 'Helaket' ? 'Crypto' : checkout.gateway} Checkout
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13.5, marginTop: 2 }}>
              {isSwychrDirect
                ? `${checkout.selectedMarket ? checkout.selectedMarket.label : 'Cameroon & Africa'} — Instant USSD push`
                : isFapshiDirect
                ? 'MTN Mobile Money or Orange Money — Cameroon'
                : 'Review your deposit, then continue to the payment provider.'}
            </div>
          </div>
        </div>

        {/* Amount summary */}
        <div className="deposit-checkout-summary-box">
          <div className="deposit-checkout-summary-row">
            <span className="deposit-checkout-summary-label">Deposit amount</span>
            <strong className="deposit-checkout-summary-val">{checkout.displayAmount}</strong>
          </div>
          <div className="deposit-checkout-summary-row">
            <span className="deposit-checkout-summary-label">Estimated credit</span>
            <strong className="deposit-checkout-summary-credit">
              {formatCurrencyWithConversion(checkout.creditedUsd || 0, 'USD', 2)}
            </strong>
          </div>
          {isDirectPay && (checkout.xafAmount || checkout.localAmount) && (
            <div className="deposit-checkout-summary-row">
              <span className="deposit-checkout-summary-label">Local amount</span>
              <strong className="deposit-checkout-summary-val">{Number(checkout.localAmount || checkout.xafAmount).toLocaleString()} {checkout.currency || 'XAF'}</strong>
            </div>
          )}
        </div>

        {/* ── Direct Pay UI (Fapshi & Swychr Direct) ── */}
        {isDirectPay && (
          <>
            {pollStatus === 'completed' ? (
              <div style={{ textAlign: 'center', padding: '28px 0' }}>
                <CheckCircle size={52} style={{ color: 'var(--green-500)', marginBottom: 16 }} />
                <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>Payment Approved!</div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 24 }}>
                  Your Mobile Money payment was confirmed. Your balance has been credited.
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/dashboard/deposit')}
                >
                  Back to Deposits
                </button>
              </div>
            ) : pollStatus === 'failed' ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <XCircle size={48} style={{ color: 'var(--red-500, #ef4444)', marginBottom: 14 }} />
                <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 8 }}>Payment Failed</div>
                {depositError && (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20, lineHeight: 1.6 }}>
                    {depositError}
                  </div>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={() => { setPollStatus(null); setDepositError(''); setPhone(''); }}
                >
                  Try Again
                </button>
              </div>
            ) : pendingId ? (
              /* Polling / waiting state */
              <div style={{ textAlign: 'center', padding: '28px 0' }}>
                <div className="deposit-checkout-spinner" />
                <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8, color: 'var(--text)' }}>
                  Waiting for your approval
                </div>
                <div style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 6, lineHeight: 1.7 }}>
                  A payment prompt was sent to <strong style={{ color: 'var(--text)' }}>{phone}</strong>.<br />
                  Approve the request with your <strong>Mobile Money PIN</strong>.
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 20 }}>
                  This page updates automatically via real-time WebSocket.
                </div>
                {networkError && (
                  <div style={{ fontSize: 13, color: 'var(--red-500, #ef4444)', marginTop: 12, fontWeight: 600 }}>
                    Connection temporarily unavailable. Retrying...
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => { setPendingId(null); setPollStatus(null); }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              /* Phone input + Deposit button */
              <>
                {depositError && (
                  <div className="alert alert-error" style={{ marginBottom: 16 }}>{depositError}</div>
                )}
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Mobile Money Number</label>
                  <input
                    className="form-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="e.g. 6XXXXXXXX"
                    autoComplete="tel"
                    disabled={depositing}
                    onKeyDown={(e) => e.key === 'Enter' && handleDirectDeposit()}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 4 }}>
                    {isSwychrDirect
                      ? `MTN MoMo or Orange Money (${checkout?.selectedMarket?.label || 'Cameroon'})`
                      : 'MTN Mobile Money or Orange Money number — Cameroon only'}
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-lg deposit-checkout-pay-btn"
                  type="button"
                  onClick={handleDirectDeposit}
                  disabled={depositing || !phone.trim()}
                >
                  {depositing ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                      <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2, borderTopColor: '#fff' }} />
                      Sending push...
                    </span>
                  ) : (
                    <span>Deposit</span>
                  )}
                </button>
              </>
            )}
          </>
        )}

        {/* ── Standard redirect UI (all other gateways) ── */}
        {!isDirectPay && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              className="btn btn-primary btn-lg deposit-checkout-pay-btn"
              type="button"
              onClick={openProviderCheckout}
            >
              <span>Continue to Payment</span>
              <ExternalLink size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Footer note */}
      <div className="deposit-checkout-info-card">
        {isDirectPay ? (
          <>
            <div>Enter your Mobile Money number and click <strong>Deposit</strong>.</div>
            <div>You will receive an instant prompt on your phone — confirm it with your PIN.</div>
            <div>Your balance updates instantly via real-time WebSocket as soon as approved.</div>
          </>
        ) : (
          <>
            <div>After your payment finishes, the provider will send you back to the deposit page automatically.</div>
            <div>If the payment is still pending when you return, your deposit history will update as soon as the gateway confirms it.</div>
          </>
        )}
      </div>
    </div>
  );
}
